import { useEffect, useState, type ChangeEvent } from "react";
import { Link } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import type { DataTableColumn } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ApiError, apiFetch } from "@/lib/api";
import type {
  ApplyGenerateFromResource,
  EntityCommitResource,
  EquipmentImportRowResource,
  ImportColumnsResource,
  ImportCommitResource,
  ImportPreviewResource,
  LeaseImportRowResource,
  LeaseTenantImportRowResource,
  MunicipalityImportRowResource,
  Page,
  PropertyImportRowResource,
  TenantImportRowResource,
  UnitImportRowResource,
} from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { labelFor } from "@/lib/field-labels";
import { todayAsDateInputValue } from "@/lib/format";

/**
 * Settings → Import (Story 8.3 — a real restructuring of Story 8.2's own
 * single-entity wizard, not an extension of it, this story's own Intent):
 * the same four-step shape (upload → map → preview → commit, spec judgement
 * call 1), generalized to accept up to six named CSVs in one run —
 * municipalities, properties, units, tenants, leases, and lease_tenants
 * (co-tenants) — any subset of which may be omitted. Uploading only
 * `leases.csv` against already-existing units reproduces Story 8.2's own
 * original scope exactly (VC-8.3-13), through this same generalized path.
 *
 * All CSV parsing happens server-side (Story 8.2's own judgement call 1,
 * unchanged): this component only ever reads an uploaded file as raw text
 * and sends it to `POST /imports/columns`/`preview`/`commit`. Nothing is
 * written until the commit step's own primary button is pressed.
 *
 * `role=Role.OWNER` gates all three routes (`app/api/imports.py`) —
 * withheld here the same way, entirely, for anyone but a confirmed Owner.
 */

//: Mirrors `app/domain/importer/multi_entity_import.py`'s own real file-key
//: order exactly (dependency order, spec judgement call 2) — referenced
//: here by literal string, never imported across the frontend/backend
//: boundary (mirrors `AddPropertyWizard.tsx`'s own established precedent).
const FILE_KEYS = [
  "municipalities",
  "properties",
  "units",
  "equipment",
  "tenants",
  "leases",
  "lease_tenants",
] as const;
type FileKey = (typeof FILE_KEYS)[number];

const FILE_TITLES: Record<FileKey, string> = {
  municipalities: "Municipalities",
  properties: "Properties",
  units: "Units",
  equipment: "Equipment",
  tenants: "Tenants",
  leases: "Leases",
  lease_tenants: "Co-tenants (lease_tenants.csv)",
};

const FILE_HELP: Record<FileKey, string> = {
  municipalities: "Towns not already in the system are created automatically even without this file.",
  properties: "Each row creates a property, and its town if it isn't already in the system.",
  units: "Each row creates one real unit on an existing or just-imported property.",
  equipment:
    "Each row fills in an existing 'Details needed' placeholder for that property/unit/category, or creates a new equipment record if none matches.",
  tenants: "Each row creates one tenant profile.",
  leases: "Each row creates one lease against an existing or just-imported unit.",
  lease_tenants: "Attaches co-tenants to a lease in this same leases.csv upload — requires leases.csv.",
};

//: Mirrors `app/domain/importer/multi_entity_import.py`'s own real
//: importable field lists exactly (order matters — the mapping step's own
//: option order).
//: Mirrors `app.domain.importer.multi_entity_import.EQUIPMENT_FIELDS`'s own
//: exact order — every field an equipment row can either fill into a
//: `Details needed` placeholder or supply outright for a brand-new row
//: (Story 12.6, R-I1d).
const EQUIPMENT_IMPORTABLE_FIELDS = [
  "property_nickname",
  "unit_label",
  "category",
  "make",
  "model_number",
  "serial_number",
  "capacity",
  "fuel_type",
  "specific_location",
  "purchase_date",
  "purchase_price_cents",
  "retailer_or_installer",
  "warranty_type",
  "warranty_length",
  "warranty_expiration_date",
  "has_extended_warranty",
  "claim_phone_number",
  "expected_useful_life_years",
  "replacement_cost_estimate_cents",
  "condition",
  "service_interval",
  "service_interval_months",
  "last_service_date",
] as const;

const IMPORTABLE_FIELDS: Record<FileKey, readonly string[]> = {
  municipalities: ["name", "county"],
  properties: ["nickname", "municipality_name", "municipality_county", "property_type", "full_address"],
  units: ["property_nickname", "label", "intent"],
  equipment: EQUIPMENT_IMPORTABLE_FIELDS,
  tenants: ["full_name", "mobile_phone", "email"],
  leases: [
    "property_nickname",
    "unit_label",
    "start_date",
    "status",
    "end_date",
    "monthly_rent_cents",
    "holdover_rent_cents",
    "notice_period_days",
  ],
  lease_tenants: ["property_nickname", "unit_label", "start_date", "tenant_full_name", "tenant_mobile_phone"],
};

const REQUIRED_FIELDS: Record<FileKey, readonly string[]> = {
  municipalities: ["name", "county"],
  properties: ["nickname", "municipality_name", "municipality_county", "property_type", "full_address"],
  units: ["property_nickname", "label", "intent"],
  equipment: ["property_nickname", "category"],
  tenants: ["full_name", "mobile_phone"],
  leases: ["property_nickname", "unit_label", "start_date", "status"],
  lease_tenants: ["property_nickname", "unit_label", "start_date", "tenant_full_name", "tenant_mobile_phone"],
};

type WizardStep = "upload" | "map" | "preview" | "commit" | "generate-from";

/** CSV column header -> target field (or `""` for "don't import this
 * column"), one map per uploaded file. `targetFieldMapping` inverts this
 * into the shape the backend expects (target field -> column) right before
 * a request is sent — mirrors Story 8.2's own identical `ColumnMapping`
 * shape, now keyed per file. */
type ColumnMapping = Record<string, string>;

function targetFieldMapping(columnMapping: ColumnMapping): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [column, field] of Object.entries(columnMapping)) {
    if (field !== "") {
      result[field] = column;
    }
  }
  return result;
}

function duplicateFieldAssignments(columnMapping: ColumnMapping): string[] {
  const counts = new Map<string, number>();
  for (const field of Object.values(columnMapping)) {
    if (field === "") continue;
    counts.set(field, (counts.get(field) ?? 0) + 1);
  }
  return [...counts.entries()].filter(([, count]) => count > 1).map(([field]) => field);
}

function cellOrDash(value: string): string {
  return value === "" ? "—" : value;
}

const MUNICIPALITY_COLUMNS: DataTableColumn<MunicipalityImportRowResource>[] = [
  { key: "row_number", sortable: false, align: "right", tabularFigures: true },
  { key: "name", sortable: false, render: (row) => cellOrDash(row.name) },
  { key: "county", sortable: false, render: (row) => cellOrDash(row.county) },
  { key: "import_error", sortable: false },
];

const PROPERTY_COLUMNS: DataTableColumn<PropertyImportRowResource>[] = [
  { key: "row_number", sortable: false, align: "right", tabularFigures: true },
  { key: "nickname", sortable: false, render: (row) => cellOrDash(row.nickname) },
  { key: "municipality_name", sortable: false, render: (row) => cellOrDash(row.municipality_name) },
  { key: "municipality_county", sortable: false, render: (row) => cellOrDash(row.municipality_county) },
  { key: "property_type", sortable: false, render: (row) => cellOrDash(row.property_type) },
  { key: "full_address", sortable: false, render: (row) => cellOrDash(row.full_address) },
  { key: "import_error", sortable: false },
];

const UNIT_COLUMNS: DataTableColumn<UnitImportRowResource>[] = [
  { key: "row_number", sortable: false, align: "right", tabularFigures: true },
  { key: "property_nickname", sortable: false, render: (row) => cellOrDash(row.property_nickname) },
  { key: "label", sortable: false, render: (row) => cellOrDash(row.label) },
  { key: "intent", sortable: false, render: (row) => cellOrDash(row.intent) },
  { key: "import_error", sortable: false },
];

const EQUIPMENT_COLUMNS: DataTableColumn<EquipmentImportRowResource>[] = [
  { key: "row_number", sortable: false, align: "right", tabularFigures: true },
  { key: "property_nickname", sortable: false, render: (row) => cellOrDash(row.property_nickname) },
  { key: "unit_label", sortable: false, render: (row) => cellOrDash(row.unit_label) },
  { key: "category", sortable: false, render: (row) => cellOrDash(row.category) },
  { key: "make", sortable: false, render: (row) => cellOrDash(row.make) },
  { key: "model_number", sortable: false, render: (row) => cellOrDash(row.model_number) },
  { key: "serial_number", sortable: false, render: (row) => cellOrDash(row.serial_number) },
  { key: "purchase_date", sortable: false, tabularFigures: true, render: (row) => cellOrDash(row.purchase_date) },
  {
    key: "warranty_expiration_date",
    sortable: false,
    tabularFigures: true,
    render: (row) => cellOrDash(row.warranty_expiration_date),
  },
  { key: "import_error", sortable: false },
];

const TENANT_COLUMNS: DataTableColumn<TenantImportRowResource>[] = [
  { key: "row_number", sortable: false, align: "right", tabularFigures: true },
  { key: "full_name", sortable: false, render: (row) => cellOrDash(row.full_name) },
  { key: "mobile_phone", sortable: false, render: (row) => cellOrDash(row.mobile_phone) },
  { key: "email", sortable: false, render: (row) => cellOrDash(row.email) },
  { key: "import_error", sortable: false },
];

const LEASE_COLUMNS: DataTableColumn<LeaseImportRowResource>[] = [
  { key: "row_number", sortable: false, align: "right", tabularFigures: true },
  { key: "property_nickname", sortable: false, render: (row) => cellOrDash(row.property_nickname) },
  { key: "unit_label", sortable: false, render: (row) => cellOrDash(row.unit_label) },
  { key: "start_date", sortable: false, tabularFigures: true, render: (row) => cellOrDash(row.start_date) },
  { key: "status", sortable: false, render: (row) => cellOrDash(row.status) },
  { key: "end_date", sortable: false, tabularFigures: true, render: (row) => cellOrDash(row.end_date) },
  {
    key: "monthly_rent_cents",
    sortable: false,
    align: "right",
    tabularFigures: true,
    render: (row) => cellOrDash(row.monthly_rent_cents),
  },
  {
    key: "holdover_rent_cents",
    sortable: false,
    align: "right",
    tabularFigures: true,
    render: (row) => cellOrDash(row.holdover_rent_cents),
  },
  {
    key: "notice_period_days",
    sortable: false,
    align: "right",
    tabularFigures: true,
    render: (row) => cellOrDash(row.notice_period_days),
  },
  { key: "import_error", sortable: false },
];

const LEASE_TENANT_COLUMNS: DataTableColumn<LeaseTenantImportRowResource>[] = [
  { key: "row_number", sortable: false, align: "right", tabularFigures: true },
  { key: "property_nickname", sortable: false, render: (row) => cellOrDash(row.property_nickname) },
  { key: "unit_label", sortable: false, render: (row) => cellOrDash(row.unit_label) },
  { key: "start_date", sortable: false, tabularFigures: true, render: (row) => cellOrDash(row.start_date) },
  { key: "tenant_full_name", sortable: false, render: (row) => cellOrDash(row.tenant_full_name) },
  { key: "tenant_mobile_phone", sortable: false, render: (row) => cellOrDash(row.tenant_mobile_phone) },
  { key: "import_error", sortable: false },
];

export function ImportWizard() {
  const isOwner = useIsOwner();

  useEffect(() => {
    document.title = "Import — PropDesk";
  }, []);

  const [step, setStep] = useState<WizardStep>("upload");

  // -- Step 1: upload ------------------------------------------------------ //
  const [fileNames, setFileNames] = useState<Partial<Record<FileKey, string>>>({});
  const [csvContents, setCsvContents] = useState<Partial<Record<FileKey, string>>>({});
  const [label, setLabel] = useState("");
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [columnsError, setColumnsError] = useState<string | undefined>(undefined);

  // -- Step 2: map columns --------------------------------------------------- //
  const [columnsByFile, setColumnsByFile] = useState<Partial<Record<FileKey, string[]>>>({});
  const [mappingByFile, setMappingByFile] = useState<Partial<Record<FileKey, ColumnMapping>>>({});
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | undefined>(undefined);

  // -- Step 3: preview -------------------------------------------------------- //
  const [previewResult, setPreviewResult] = useState<ImportPreviewResource | null>(null);

  // -- Step 4: commit ---------------------------------------------------------- //
  const [isCommitting, setIsCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | undefined>(undefined);
  const [commitResult, setCommitResult] = useState<ImportCommitResource | null>(null);

  // -- Step 5: start generating from a date (Story 8.5) --------------------- //
  const [generateFromDate, setGenerateFromDate] = useState(todayAsDateInputValue);
  const [isApplyingGenerateFrom, setIsApplyingGenerateFrom] = useState(false);
  const [generateFromError, setGenerateFromError] = useState<string | undefined>(undefined);
  const [generateFromResult, setGenerateFromResult] = useState<ApplyGenerateFromResource | null>(null);
  const [generateFromSkipped, setGenerateFromSkipped] = useState(false);

  if (!isOwner) {
    return (
      <div className="max-w-record-column-max">
        <ReadOnlyNotice className="mb-4" />
        <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Import</h1>
        <p className="mt-4 text-body text-ink-secondary">Only the account owner can import leases.</p>
      </div>
    );
  }

  const uploadedFileKeys = FILE_KEYS.filter((key) => csvContents[key] !== undefined);

  async function handleFileSelected(fileKey: FileKey, event: ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files?.[0];
    if (!selected) {
      return;
    }
    const text = await selected.text();
    setFileNames((existing) => ({ ...existing, [fileKey]: selected.name }));
    setCsvContents((existing) => ({ ...existing, [fileKey]: text }));
    if (label.trim() === "") {
      setLabel(selected.name.replace(/\.csv$/i, ""));
    }
  }

  async function handleUploadNext() {
    if (uploadedFileKeys.length === 0) {
      return;
    }
    setIsLoadingColumns(true);
    setColumnsError(undefined);
    try {
      const files: Record<string, string> = {};
      for (const key of uploadedFileKeys) {
        files[key] = csvContents[key] as string;
      }
      const result = await apiFetch<ImportColumnsResource>("/imports/columns", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files }),
      });
      setColumnsByFile(result as Partial<Record<FileKey, string[]>>);
      setStep("map");
    } catch (error) {
      setColumnsError(error instanceof ApiError ? error.message : "Something went wrong. Please try again.");
    } finally {
      setIsLoadingColumns(false);
    }
  }

  async function handleMapNext() {
    setIsLoadingPreview(true);
    setPreviewError(undefined);
    try {
      const files: Record<string, string> = {};
      const mappings: Record<string, Record<string, string>> = {};
      for (const key of uploadedFileKeys) {
        files[key] = csvContents[key] as string;
        mappings[key] = targetFieldMapping(mappingByFile[key] ?? {});
      }
      const result = await apiFetch<ImportPreviewResource>("/imports/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files, mappings }),
      });
      setPreviewResult(result);
      setStep("preview");
    } catch (error) {
      setPreviewError(error instanceof ApiError ? error.message : "Something went wrong. Please try again.");
    } finally {
      setIsLoadingPreview(false);
    }
  }

  async function handleCommit() {
    setIsCommitting(true);
    setCommitError(undefined);
    try {
      const files: Record<string, string> = {};
      const mappings: Record<string, Record<string, string>> = {};
      for (const key of uploadedFileKeys) {
        files[key] = csvContents[key] as string;
        mappings[key] = targetFieldMapping(mappingByFile[key] ?? {});
      }
      const result = await apiFetch<ImportCommitResource>("/imports/commit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files, mappings, label: label.trim() }),
      });
      setCommitResult(result);
      // Story 8.5, judgement call 4: the AC's own "explicit, separate step"
      // right after the wizard finishes — a real 5th step, not a link to a
      // different screen.
      setStep("generate-from");
    } catch (error) {
      setCommitError(error instanceof ApiError ? error.message : "Something went wrong. Please try again.");
    } finally {
      setIsCommitting(false);
    }
  }

  async function handleApplyGenerateFrom() {
    if (commitResult === null) {
      return;
    }
    setIsApplyingGenerateFrom(true);
    setGenerateFromError(undefined);
    try {
      const result = await apiFetch<ApplyGenerateFromResource>(
        `/import-batches/${commitResult.import_batch_id}/generate-from`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ generate_from: generateFromDate }),
        },
      );
      setGenerateFromResult(result);
    } catch (error) {
      setGenerateFromError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again.",
      );
    } finally {
      setIsApplyingGenerateFrom(false);
    }
  }

  function handleSkipGenerateFrom() {
    setGenerateFromSkipped(true);
  }

  function handleStartOver() {
    setStep("upload");
    setFileNames({});
    setCsvContents({});
    setLabel("");
    setColumnsError(undefined);
    setColumnsByFile({});
    setMappingByFile({});
    setPreviewError(undefined);
    setPreviewResult(null);
    setCommitError(undefined);
    setCommitResult(null);
    setGenerateFromDate(todayAsDateInputValue());
    setIsApplyingGenerateFrom(false);
    setGenerateFromError(undefined);
    setGenerateFromResult(null);
    setGenerateFromSkipped(false);
  }

  // -- Derived state --------------------------------------------------------- //

  let anyDuplicates = false;
  let everyRequiredFieldAssignedEverywhere = true;
  for (const key of uploadedFileKeys) {
    const mapping = mappingByFile[key] ?? {};
    if (duplicateFieldAssignments(mapping).length > 0) {
      anyDuplicates = true;
    }
    const assigned = new Set(Object.values(mapping).filter((field) => field !== ""));
    if (!REQUIRED_FIELDS[key].every((field) => assigned.has(field))) {
      everyRequiredFieldAssignedEverywhere = false;
    }
  }
  const canAdvanceFromMap = uploadedFileKeys.length > 0 && everyRequiredFieldAssignedEverywhere && !anyDuplicates;

  let totalValidCount = 0;
  let totalRowCount = 0;
  if (previewResult) {
    for (const key of FILE_KEYS) {
      const page = previewResult[key] as Page<{ import_error: string | null }> | null | undefined;
      if (!page) continue;
      totalRowCount += page.meta.total;
      totalValidCount += page.data.filter((row) => row.import_error === null).length;
    }
  }

  const heading =
    step === "generate-from"
      ? "Import complete — start generating charges"
      : commitResult
        ? "Import complete"
        : step === "upload"
          ? "Import — upload files"
          : step === "map"
            ? "Import — map columns"
            : step === "preview"
              ? "Import — preview"
              : "Import — commit";

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <h1 className="text-title font-semibold tracking-title text-ink md:hidden">{heading}</h1>

      {step === "upload" ? (
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleUploadNext();
          }}
        >
          <p className="text-body text-ink-secondary">
            Upload any combination of the files below — municipalities, properties, units, tenants, leases,
            and lease co-tenants. Nothing is created until the last step.
          </p>
          {FILE_KEYS.map((key) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{FILE_TITLES[key]}</span>
              <span className="text-meta text-ink-secondary">{FILE_HELP[key]}</span>
              <input
                type="file"
                accept=".csv,text/csv"
                aria-label={FILE_TITLES[key]}
                onChange={(event) => void handleFileSelected(key, event)}
                className="text-body text-ink"
              />
              {fileNames[key] !== undefined ? (
                <span className="text-meta text-ink-secondary">Selected: {fileNames[key]}</span>
              ) : null}
            </label>
          ))}
          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">Name this import</span>
            <Input value={label} onChange={(event) => setLabel(event.target.value)} />
          </label>
          {columnsError ? (
            <p role="alert" className="text-meta text-danger">
              {columnsError}
            </p>
          ) : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={uploadedFileKeys.length === 0 || label.trim() === "" || isLoadingColumns}>
              {isLoadingColumns ? "Reading…" : "Next"}
            </Button>
          </div>
        </form>
      ) : null}

      {step === "map" ? (
        <form
          className="mt-4 flex flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            void handleMapNext();
          }}
        >
          {uploadedFileKeys.map((key) => (
            <div key={key} className="flex flex-col gap-3">
              <h2 className="text-body font-semibold text-ink">{FILE_TITLES[key]}</h2>
              {(columnsByFile[key] ?? []).map((column) => (
                <label key={column} className="flex flex-col gap-1">
                  <span className="text-label font-medium text-ink-secondary">{column}</span>
                  <Select
                    aria-label={column}
                    value={(mappingByFile[key] ?? {})[column] ?? ""}
                    onChange={(event) =>
                      setMappingByFile((existing) => ({
                        ...existing,
                        [key]: { ...(existing[key] ?? {}), [column]: event.target.value },
                      }))
                    }
                  >
                    <option value="">Don&apos;t import this column</option>
                    {IMPORTABLE_FIELDS[key].map((field) => (
                      <option key={field} value={field}>
                        {labelFor(field)}
                        {REQUIRED_FIELDS[key].includes(field) ? " (required)" : ""}
                      </option>
                    ))}
                  </Select>
                </label>
              ))}
              {duplicateFieldAssignments(mappingByFile[key] ?? {}).length > 0 ? (
                <p role="alert" className="text-meta text-danger">
                  Each field can only come from one column:{" "}
                  {duplicateFieldAssignments(mappingByFile[key] ?? {})
                    .map((field) => labelFor(field))
                    .join(", ")}
                  .
                </p>
              ) : null}
            </div>
          ))}
          {previewError ? (
            <p role="alert" className="text-meta text-danger">
              {previewError}
            </p>
          ) : null}
          <div className="flex justify-between gap-2">
            <Button type="button" variant="ghost" onClick={() => setStep("upload")}>
              Back
            </Button>
            <Button type="submit" disabled={!canAdvanceFromMap || isLoadingPreview}>
              {isLoadingPreview ? "Checking…" : "Next"}
            </Button>
          </div>
        </form>
      ) : null}

      {step === "preview" ? (
        <div className="mt-4 flex flex-col gap-6">
          <p className="text-body text-ink">
            {totalValidCount} of {totalRowCount} row{totalRowCount === 1 ? "" : "s"} will be created.
          </p>
          {totalValidCount === 0 ? (
            <p role="alert" className="text-body text-danger">
              None of these rows can be imported yet. Go back and fix the file(s) or the column mapping before
              committing.
            </p>
          ) : null}

          {previewResult?.municipalities ? (
            <DataTable
              caption="Municipalities"
              itemNamePlural="rows"
              columns={MUNICIPALITY_COLUMNS}
              page={previewResult.municipalities}
              isPending={false}
              isFetching={false}
              isError={false}
              emptyState={{ heading: "No rows found.", body: "Check the file has data rows below its header." }}
            />
          ) : null}
          {previewResult?.properties ? (
            <DataTable
              caption="Properties"
              itemNamePlural="rows"
              columns={PROPERTY_COLUMNS}
              page={previewResult.properties}
              isPending={false}
              isFetching={false}
              isError={false}
              emptyState={{ heading: "No rows found.", body: "Check the file has data rows below its header." }}
            />
          ) : null}
          {previewResult?.units ? (
            <DataTable
              caption="Units"
              itemNamePlural="rows"
              columns={UNIT_COLUMNS}
              page={previewResult.units}
              isPending={false}
              isFetching={false}
              isError={false}
              emptyState={{ heading: "No rows found.", body: "Check the file has data rows below its header." }}
            />
          ) : null}
          {previewResult?.equipment ? (
            <DataTable
              caption="Equipment"
              itemNamePlural="rows"
              columns={EQUIPMENT_COLUMNS}
              page={previewResult.equipment}
              isPending={false}
              isFetching={false}
              isError={false}
              emptyState={{ heading: "No rows found.", body: "Check the file has data rows below its header." }}
            />
          ) : null}
          {previewResult?.tenants ? (
            <DataTable
              caption="Tenants"
              itemNamePlural="rows"
              columns={TENANT_COLUMNS}
              page={previewResult.tenants}
              isPending={false}
              isFetching={false}
              isError={false}
              emptyState={{ heading: "No rows found.", body: "Check the file has data rows below its header." }}
            />
          ) : null}
          {previewResult?.leases ? (
            <DataTable
              caption="Leases"
              itemNamePlural="rows"
              columns={LEASE_COLUMNS}
              page={previewResult.leases}
              isPending={false}
              isFetching={false}
              isError={false}
              emptyState={{ heading: "No rows found.", body: "Check the file has data rows below its header." }}
            />
          ) : null}
          {previewResult?.lease_tenants ? (
            <DataTable
              caption="Co-tenants"
              itemNamePlural="rows"
              columns={LEASE_TENANT_COLUMNS}
              page={previewResult.lease_tenants}
              isPending={false}
              isFetching={false}
              isError={false}
              emptyState={{ heading: "No rows found.", body: "Check the file has data rows below its header." }}
            />
          ) : null}

          <div className="flex justify-between gap-2">
            <Button type="button" variant="ghost" onClick={() => setStep("map")}>
              Back
            </Button>
            <Button type="button" onClick={() => setStep("commit")} disabled={totalValidCount === 0}>
              Next
            </Button>
          </div>
        </div>
      ) : null}

      {step === "commit" ? (
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-body text-ink">
            Importing will create {totalValidCount} record{totalValidCount === 1 ? "" : "s"}. Every side
            effect this would normally trigger — alerts, and the future rent-charge generator — is
            suppressed for everything this import creates. Once leases exist, a separate step lets you
            choose the date each one should start generating charges from — that step follows right after
            this one.
          </p>
          <p className="text-body font-medium text-ink">This step cannot be undone.</p>
          {commitError ? (
            <p role="alert" className="text-meta text-danger">
              {commitError}
            </p>
          ) : null}
          <div className="flex justify-between gap-2">
            <Button type="button" variant="ghost" onClick={() => setStep("preview")} disabled={isCommitting}>
              Back
            </Button>
            <Button type="button" onClick={() => void handleCommit()} disabled={isCommitting}>
              {isCommitting ? "Importing…" : `Import ${totalValidCount} record${totalValidCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      ) : null}

      {step === "generate-from" && commitResult ? (
        <div className="mt-4 flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            {FILE_KEYS.filter((key) => commitResult[key] !== undefined && commitResult[key] !== null).map(
              (key) => {
                const entity = commitResult[key] as EntityCommitResource;
                return (
                  <div key={key} className="flex flex-col gap-2">
                    <p className="text-body text-ink">
                      {FILE_TITLES[key]}: {entity.created_count} of {entity.total_rows} row
                      {entity.total_rows === 1 ? "" : "s"} imported.
                      {entity.failures.length > 0 ? ` ${entity.failures.length} couldn't be saved:` : ""}
                    </p>
                    {entity.failures.length > 0 ? (
                      <ul className="flex flex-col gap-1">
                        {entity.failures.map((failure) => (
                          <li key={failure.row_number} className="text-body text-ink">
                            Row {failure.row_number}: {failure.reason}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                );
              },
            )}
            <p className="text-meta text-ink-secondary">Import batch: {commitResult.import_batch_id}</p>
          </div>

          {generateFromResult ? (
            <div className="flex flex-col gap-4">
              <p className="text-body text-ink">
                {generateFromResult.leases_updated} lease{generateFromResult.leases_updated === 1 ? "" : "s"}{" "}
                will start generating charges from {generateFromResult.generate_from}. This date can&apos;t be
                changed through this screen once it&apos;s set — a lease that already had one kept it instead.
              </p>
              <div className="flex justify-end">
                <Button type="button" onClick={handleStartOver}>
                  Start another import
                </Button>
              </div>
            </div>
          ) : generateFromSkipped ? (
            <div className="flex flex-col gap-4">
              <p className="text-body text-ink">
                Skipped. These leases won&apos;t generate any charges until a date is set — from the{" "}
                <Link to="/settings/import-batches" className="underline">
                  Import batches
                </Link>{" "}
                screen, whenever you&apos;re ready.
              </p>
              <div className="flex justify-end">
                <Button type="button" onClick={handleStartOver}>
                  Start another import
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-body text-ink">
                Choose the date these leases should start generating charges from — so the ledger begins on
                that date rather than back when the lease itself started. Skip this and it stays off; nothing
                is generated until a date is set, here or later from the Import batches screen. A lease that
                already has a date keeps it — this never overwrites an earlier date, even a different one —
                and once a lease has one, it can&apos;t be changed through this screen.
              </p>
              <label className="flex max-w-xs flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">Start generating charges from</span>
                <Input
                  type="date"
                  value={generateFromDate}
                  onChange={(event) => setGenerateFromDate(event.target.value)}
                />
              </label>
              {generateFromError ? (
                <p role="alert" className="text-meta text-danger">
                  {generateFromError}
                </p>
              ) : null}
              <div className="flex justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSkipGenerateFrom}
                  disabled={isApplyingGenerateFrom}
                >
                  Skip
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleApplyGenerateFrom()}
                  disabled={isApplyingGenerateFrom || generateFromDate === ""}
                >
                  {isApplyingGenerateFrom ? "Applying…" : "Apply"}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
